import React, { useEffect, useState } from 'react';
import {
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Typography
} from '@mui/material';
import {
    Folder as FolderIcon,
    ExpandLess,
    ExpandMore,
} from '@mui/icons-material';

interface MediaItem {
    id_media: number;
    media_name: string;
    total_file_count: number;
    children?: MediaItem[];
}

interface FolderTreeProps {
    data: MediaItem[];
    level?: number;
    onClick?: (id: number) => void;
    id_media?: number | null;
    autoExpandToId?: number | null;
}

export const FolderTree: React.FC<FolderTreeProps> = ({
    data,
    level = 0,
    onClick,
    id_media,
    autoExpandToId,
}) => {
    const [openItems, setOpenItems] = useState<Record<number, boolean>>({});

    const toggle = (id: number) => {
        setOpenItems((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleClick = (item: MediaItem) => {
        onClick?.(item.id_media); // ส่งออกไป
        if (item.children?.length) {
            toggle(item.id_media); // toggle ถ้ามี children
        }
    };

    const findPathById = (
        nodes: MediaItem[],
        targetId: number,
        path: number[] = []
    ): number[] | null => {
        for (const node of nodes) {
            const currentPath = [...path, node.id_media];
            if (node.id_media === targetId) return currentPath;
            if (node.children) {
                const childPath = findPathById(node.children, targetId, currentPath);
                if (childPath) return childPath;
            }
        }
        return null;
    };


    useEffect(() => {
        if (autoExpandToId != null) {
            const path = findPathById(data, autoExpandToId);
            if (path) {
                const expandMap: Record<number, boolean> = {};
                path.slice(0, -1).forEach((id) => {
                    expandMap[id] = true;
                });
                setOpenItems((prev) => ({ ...prev, ...expandMap }));
            }
        }
    }, [autoExpandToId, data]);


    return (
        <List disablePadding>
            {data.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isOpen = !!openItems[item.id_media];
                const isSelected = id_media === item.id_media;

                return (
                    <React.Fragment key={item.id_media}>
                        <ListItemButton
                            selected={isSelected}
                            onClick={() => handleClick(item)}
                            sx={{
                                pl: 2 + level * 2,
                                bgcolor: isSelected ? 'action.selected' : 'inherit',
                            }}
                        >
                            <ListItemIcon>
                                <FolderIcon color={isSelected ? 'primary' : 'action'} />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography variant="body2">
                                        {item.media_name} ({item.total_file_count})
                                    </Typography>
                                }
                            />
                            {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItemButton>

                        {hasChildren && (
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                <FolderTree
                                    data={item.children!}
                                    level={level + 1}
                                    id_media={id_media}
                                    onClick={onClick}
                                />
                            </Collapse>
                        )}
                    </React.Fragment>
                );
            })}
        </List>
    );
};
